import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getActiveSubsystem } from '@bemap/utils';
import { getModuleDefinitions, getSubsystemLinks } from '../constants/links';
import { TopbarTab } from '../types/dashboard';
import { ISession, RoleLevel } from '@bemap/utils';

// Check if user has admin permission (SISADM or ADMIN)
const hasAdminPermission = (user: ISession | null): boolean => {
  if (!user) return false;
  return user.permission === RoleLevel.SISADM || user.permission === RoleLevel.ADMIN;
};

export const useDashboard = (user: ISession | null) => {
  const location = useLocation();
  const { t } = useTranslation();

  const availableModules = useMemo(() => {
    if (!user) return [];

    const moduleDefinitions = getModuleDefinitions(t);
    const modules: any[] = [];

    // Add modules based on user.modules access
    if (user.modules) {
      Object.entries(user.modules)
        .filter(([_, hasAccess]) => hasAccess)
        .forEach(([moduleId]) => {
          const module = moduleDefinitions[moduleId as keyof typeof moduleDefinitions];
          if (module) {
            modules.push(module);
          }
        });
    }

    // Add admin module if user has admin permission
    if (hasAdminPermission(user)) {
      const adminModule = moduleDefinitions.admin;
      if (adminModule && !modules.find(m => m.id === 'admin')) {
        modules.push(adminModule);
      }
    }

    return modules;
  }, [user, t]);

  const topbarTabs: TopbarTab[] = useMemo(() => {
    return availableModules.map(module => ({
      id: module.id,
      label: module.label,
      href: module.href,
      onClick: () => {
        // This will be handled by the component using this hook
        window.location.href = module.href;
      },
    }));
  }, [availableModules]);

  const activeSubsystem = getActiveSubsystem(location.pathname);
  const subsystemLinks = getSubsystemLinks(t);
  const currentSidebarLinks = subsystemLinks[activeSubsystem] || subsystemLinks.admin;

  return {
    availableModules,
    topbarTabs,
    activeSubsystem,
    currentSidebarLinks,
  };
}; 